import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, History, Loader2, Save, Sparkles, X } from "lucide-react";

/* ===================== COMPONENTS ===================== */
import Input from "../../../components/Input/Input";
import Notification from "../../../components/Notification/Notification";

/* ===================== SERVICES ===================== */
import { apiGet, apiPut } from "../../../apiService";
import { getErrorMessage } from "../../../utils/errorHelper";

/* ===================== STYLES ===================== */
import styles from "./GlobalSettings.module.scss";

const BACK_PATH = "/admin/billing-management";

const formatDateTime = (iso) => {
  if (!iso) return "--";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "--";
  return d.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const GlobalSettings = () => {
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [form, setForm] = useState({ newRate: "", reason: "" });
  const [errors, setErrors] = useState({});

  const [notification, setNotification] = useState("");
  const [notificationType, setNotificationType] = useState("success");

  const showNotification = (message, type = "success") => {
    setNotification(message);
    setNotificationType(type);
    setTimeout(() => setNotification(""), 3000);
  };

  const fetchGlobalRate = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiGet("catalog", "/billing/global-rate");
      if (res?.status !== "success") {
        throw new Error(
          JSON.stringify({
            message: res?.message || "Failed to load global rate",
          }),
        );
      }
      setData(res.data || null);
      setForm({
        newRate: res.data?.currentRate ?? "",
        reason: "",
      });
    } catch (err) {
      showNotification(
        getErrorMessage(err, "Failed to load global rate"),
        "error",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchGlobalRate();
  }, [fetchGlobalRate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validate = () => {
    const next = {};
    const rateNum = Number(form.newRate);
    if (form.newRate === "" || Number.isNaN(rateNum) || rateNum <= 0) {
      next.newRate = "Enter a valid rate greater than 0";
    }
    if (!String(form.reason).trim()) {
      next.reason = "Please provide a reason for the change";
    }
    return next;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const nextErrors = validate();
    if (Object.keys(nextErrors).length) {
      setErrors(nextErrors);
      return;
    }

    const newRate = Number(form.newRate);
    if (
      data?.currentRate !== undefined &&
      newRate === Number(data.currentRate)
    ) {
      setErrors({
        newRate: "New rate is the same as the current rate",
      });
      return;
    }

    setSubmitting(true);
    try {
      const res = await apiPut("catalog", "/billing/global-rate", {
        newRate,
        reason: String(form.reason).trim(),
      });
      if (res?.status === "success") {
        showNotification(
          res?.message || "Global rate updated successfully",
          "success",
        );
        setForm((prev) => ({ ...prev, reason: "" }));
        fetchGlobalRate();
      } else {
        throw new Error(
          JSON.stringify({
            message: res?.message || "Failed to update global rate",
          }),
        );
      }
    } catch (err) {
      showNotification(
        getErrorMessage(err, "Failed to update global rate"),
        "error",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={styles["global-settings__page"]}>
      {notification && (
        <div className={styles["global-settings__toast-container"]}>
          <Notification message={notification} type={notificationType} />
        </div>
      )}

      <div className={styles["global-settings__header"]}>
        <Link to={BACK_PATH} className={styles["global-settings__back-btn"]}>
          <ArrowLeft size={20} strokeWidth={2.5} />
        </Link>
        <div className={styles["global-settings__title-group"]}>
          <h2 className={styles["global-settings__title"]}>
            Update Global Settings
          </h2>
          <p className={styles["global-settings__subtitle"]}>
            Manage the global credit rate that applies to all new purchases.
          </p>
        </div>
      </div>

      {loading ? (
        <div className={styles["global-settings__loading"]}>
          <Loader2 className={styles["global-settings__spin"]} size={28} />
          <span>Loading global settings…</span>
        </div>
      ) : (
        <>
          {/* CURRENT STATE */}
          <div className={styles["global-settings__current-card"]}>
            <div className={styles["global-settings__current-icon"]}>
              <Sparkles size={20} strokeWidth={2.5} />
            </div>
            <div className={styles["global-settings__current-info"]}>
              <span className={styles["global-settings__current-label"]}>
                Current Global Rate
              </span>
              <span className={styles["global-settings__current-value"]}>
                ₹{data?.currentRate ?? "--"}
                <span className={styles["global-settings__current-suffix"]}>
                  / credit
                </span>
              </span>
            </div>
            <div className={styles["global-settings__current-meta"]}>
              <span>Last updated</span>
              <strong>{formatDateTime(data?.lastUpdatedAt)}</strong>
            </div>
          </div>

          {/* UPDATE FORM */}
          <div className={styles["global-settings__card"]}>
            <h3 className={styles["global-settings__card-title"]}>
              Update Rate
            </h3>
            <p className={styles["global-settings__card-sub"]}>
              Changes will apply to all subsequent credit purchases. The
              previous rate will be archived in the history below.
            </p>

            <form
              onSubmit={handleSubmit}
              noValidate
              className={styles["global-settings__form"]}
            >
              <div className={styles["global-settings__grid"]}>
                <Input
                  label="New Rate (₹ per credit)"
                  type="number"
                  name="newRate"
                  placeholder="e.g. 10"
                  value={form.newRate}
                  onChange={handleChange}
                  error={errors.newRate}
                  required
                  min={0}
                  step="0.01"
                />

                <Input
                  label="Reason"
                  name="reason"
                  placeholder="e.g. Q3 price update"
                  value={form.reason}
                  onChange={handleChange}
                  error={errors.reason}
                  required
                />
              </div>

              <div className={styles["global-settings__actions"]}>
                <button
                  type="button"
                  className={`${styles["global-settings__btn"]} ${styles["global-settings__btn--secondary"]}`}
                  onClick={() => navigate(BACK_PATH)}
                  disabled={submitting}
                >
                  <X size={16} strokeWidth={2.5} />
                  Cancel
                </button>

                <button
                  type="submit"
                  className={`${styles["global-settings__btn"]} ${styles["global-settings__btn--primary"]}`}
                  disabled={submitting}
                >
                  <Save size={16} strokeWidth={2.5} />
                  {submitting ? "Updating…" : "Update Rate"}
                </button>
              </div>
            </form>
          </div>

          {/* HISTORY */}
          <div className={styles["global-settings__card"]}>
            <div className={styles["global-settings__history-header"]}>
              <div className={styles["global-settings__history-icon"]}>
                <History size={18} strokeWidth={2.5} />
              </div>
              <h3 className={styles["global-settings__card-title"]}>
                Rate Change History
              </h3>
            </div>

            {Array.isArray(data?.history) && data.history.length > 0 ? (
              <ul className={styles["global-settings__history-list"]}>
                {data.history.map((entry) => {
                  const oldRate = Number(entry.oldRate);
                  const newRate = Number(entry.newRate);
                  const trendDown = newRate < oldRate;
                  return (
                    <li
                      key={entry.id}
                      className={styles["global-settings__history-item"]}
                    >
                      <div
                        className={styles["global-settings__history-rate-row"]}
                      >
                        <span
                          className={
                            styles["global-settings__history-rate-old"]
                          }
                        >
                          ₹{entry.oldRate}
                        </span>
                        <span
                          className={`${styles["global-settings__history-arrow"]} ${
                            trendDown
                              ? styles["global-settings__history-arrow--down"]
                              : styles["global-settings__history-arrow--up"]
                          }`}
                        >
                          →
                        </span>
                        <span
                          className={
                            styles["global-settings__history-rate-new"]
                          }
                        >
                          ₹{entry.newRate}
                        </span>
                        <span
                          className={styles["global-settings__history-when"]}
                        >
                          {formatDateTime(entry.changedAt)}
                        </span>
                      </div>
                      {entry.reason && (
                        <p
                          className={styles["global-settings__history-reason"]}
                        >
                          {entry.reason}
                        </p>
                      )}
                    </li>
                  );
                })}
              </ul>
            ) : (
              <p className={styles["global-settings__history-empty"]}>
                No rate changes recorded yet.
              </p>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default GlobalSettings;
